/** The knock, end to end: start, turns, receipt, standing (M8–M12). Split from the api.ts monolith 2026-08-03; the barrel re-exports. Attachments left for `attachment.ts` when the doorman thread learned to take them too. The ceiling call is gone (economics-wire §5.2): the hold placed at intake covers the worst case a hearing can cost, so a hearing that opened can always be finished. */
import {
  KnockGate as SharedKnockGateSchema,
  type KnockGate as SharedKnockGate,
} from "@nob/schemas";
import { type Attachment, toAttachment } from "./attachment";
import { type HearingRoads, normaliseHearingRoads } from "./plan";
import {
  type RawError,
  bareHandle,
  call,
  detailsOf,
  get,
  num,
  optTextOf,
  post,
  textOf,
} from "./core";
// --- knocking (M8, PRD §5.3 / FR-3.3–FR-3.4) --------------------------------

/**
 * The sender's side. The door is read without a session — it is the same card
 * the public page renders (§5.5) — and then the knock itself, which is the one
 * call in this app that commits money. Every refusal therefore carries the
 * detail its screen needs to answer it, rather than a bare code.
 */

/**
 * The public card, and what it deliberately does not carry: a price, or any
 * figure a price could be recovered from.
 *
 * A DOOR HAS NO PRICE AND CANNOT BE GIVEN ONE (ruling C3). What survives of the
 * measured band is `difficulty` — a SENTENCE the server already decided
 * (economics-wire §6.1), never `{p25, p75}`. The band itself does not travel: a
 * payload carrying the percentiles would put ruling CR one `fetch` away from
 * being broken, since a client could print the numbers the page stopped
 * printing, or sort doors by them.
 */
export type PublicDoor = {
  handle: string;
  display_name: string;
  /** The picture, as a path on OUR origin, or null for the monogram (FR-3.8.2). */
  avatar: string | null;
  /** The owner's own line (FR-3.8.1), or null for a door that has none. */
  bio: string | null;
  persona_name: string | null;
  /**
   * WHAT GETS THROUGH, AS STRANGERS READ IT (ruling R-0910-10) — the owner's
   * own public line, and `null` for a door that has not written one, which
   * every surface renders as no such block at all.
   *
   * IT IS NOT A SECTION OF THE HOUSE RULES, and `gets_through` is gone rather
   * than renamed: that array published one section of a private document, and
   * no section of it reaches any public surface any more.
   */
  public_line: string | null;
  /**
   * HOW HARD THIS DOOR IS, AS PROSE, from one server function. Always a
   * sentence — an unmeasured door says so in words ("Not enough knocks here yet
   * to say how hard this door is") rather than going quiet. The web computes
   * nothing from it and has nothing to compute it from.
   */
  difficulty: string;
  /**
   * THE AI DISCLOSURE (EU AI Act Art. 50; ruling R-0914-1 (11)). Fixed,
   * server-authored, the same on every door — and it arrives HERE as well as on
   * the knock payloads because the console draws the thread column from this
   * read while the knock is still a draft, which is the first moment a stranger
   * reads anything the doorman is about to say. Empty when the payload could not
   * be read, which draws nothing.
   */
  disclosure: string;
};

/** Paused, gone, or never there — three different things to say (FR-3.1). */
export type PublicDoorFailureCode =
  "DOOR_NOT_FOUND" | "DOOR_SUPPRESSED" | "DOOR_CLOSED" | "UNKNOWN";

export type PublicDoorResult =
  { ok: true; door: PublicDoor } | { ok: false; code: PublicDoorFailureCode };

/**
 * A picture this app is willing to render (FR-3.8.2).
 *
 * The rule is "every image is served from OUR API", and a rule enforced only by
 * the server is enforced once. This is the second place: a value that is not a
 * relative `/api/v1/avatars/…` path — an absolute URL, a protocol-relative
 * `//cdn/…`, anything else — becomes null, so no payload can talk this client
 * into a request to somebody else's host.
 *
 * THE PATH IS USED AS THE SERVER GAVE IT: root-relative, on this origin. It
 * used to be prefixed with the api's absolute address, because deployed there
 * were two hosts and a relative path asked the APP for a picture (2026-09-08).
 * There is one origin now (ruling R-0926-1 (1)), and `/api/…` is the api's on
 * it, so the prefix had nothing left to do.
 */
export function ownOriginAvatar(raw: unknown): string | null {
  return typeof raw === "string" && /^\/api\/v1\/avatars\/[0-9a-f]{64}\.(jpg|png|webp)$/.test(raw)
    ? raw
    : null;
}

export async function fetchPublicDoor(handle: string): Promise<PublicDoorResult> {
  const r = await call(() => get(`/api/v1/door/${encodeURIComponent(bareHandle(handle))}/public`));
  if (!r) return { ok: false, code: "UNKNOWN" };

  if (!r.res.ok) {
    const code = (r.body as RawError).error?.code;
    if (r.res.status === 410 || code === "DOOR_SUPPRESSED") {
      return { ok: false, code: "DOOR_SUPPRESSED" };
    }
    if (r.res.status === 409 || code === "DOOR_CLOSED") return { ok: false, code: "DOOR_CLOSED" };
    if (r.res.status === 404) return { ok: false, code: "DOOR_NOT_FOUND" };
    return { ok: false, code: "UNKNOWN" };
  }

  const b = r.body as Record<string, unknown>;
  const wire = typeof b["handle"] === "string" ? b["handle"] : bareHandle(handle);
  const persona = typeof b["persona_name"] === "string" ? b["persona_name"].trim() : "";
  return {
    ok: true,
    door: {
      handle: wire,
      // A door with no display name is still a door — it is addressed by handle.
      display_name:
        typeof b["display_name"] === "string" && b["display_name"].trim()
          ? b["display_name"]
          : `@${wire}`,
      avatar: ownOriginAvatar(b["avatar"]),
      bio: optTextOf(b["bio"]),
      persona_name: persona || null,
      public_line: optTextOf(b["public_line"]),
      difficulty: textOf(b["difficulty"]),
      disclosure: textOf(b["disclosure"]),
    },
  };
}

/**
 * What the sender hands over about themselves (§5.3). Only the email is
 * required — and the door-requested channels are gone with the ask itself
 * (ruling R-0828-2): there is no `contacts` bag, because there is nothing a
 * door may ask a knocker to be reachable on besides the console.
 */
export type SenderProfile = {
  reply_to_email: string;
  booking_url?: string;
  company_url?: string;
};

export type KnockStartInput = {
  door_handle: string;
  sender_profile: SenderProfile;
  /**
   * The anonymous key this visit wrote its held draft under, when there was
   * one. PROVENANCE ONLY: the words themselves still travel the ordinary way —
   * typed into the composer and sent as a turn — so nothing about what gets
   * screened, or when, moves because a knock began before an account did.
   */
  anon_key?: string;
  /**
   * The words the knocker wrote go up as the first turn straight after this
   * call (wave 82), so the doorman asks no opening question: it would land in
   * the thread above words that were written without it.
   */
  knocker_speaks_first?: boolean;
};

/**
 * The held draft (schema: `held_drafts`) — a knock written BEFORE there is an
 * account to write it under.
 *
 * Both calls are unauthenticated by design: the whole point is that they happen
 * on the wrong side of a sign-in. The `anon_key` is a uuid this browser minted
 * and kept, so the round trip out to X and back can find the words again; the
 * server holds them for a while and forgets them. Neither call is authoritative
 * about anything — the local stash in `lib/draft` is the fallback whenever the
 * server declines, because the promise being kept here ("Nob did not lose what
 * you wrote") must not depend on a network that may be having a bad minute.
 */
export type HoldDraftResult =
  { ok: true; expires_at: string | null } | { ok: false; code: "VALIDATION_ERROR" | "UNKNOWN" };

export async function holdDraft(input: {
  anon_key: string;
  door_handle: string;
  body: string;
}): Promise<HoldDraftResult> {
  const r = await call(() =>
    post("/api/v1/draft", {
      anon_key: input.anon_key,
      door_handle: bareHandle(input.door_handle),
      body: input.body,
    }),
  );
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    return { ok: false, code: r.res.status === 400 ? "VALIDATION_ERROR" : "UNKNOWN" };
  }
  return { ok: true, expires_at: optTextOf((r.body as Record<string, unknown>)["expires_at"]) };
}

/** The words back, or `null` — an empty hand is not a failure, it is no draft. */
export async function fetchHeldDraft(input: {
  anon_key: string;
  door_handle: string;
}): Promise<string | null> {
  const query = `anon_key=${encodeURIComponent(input.anon_key)}&door_handle=${encodeURIComponent(
    bareHandle(input.door_handle),
  )}`;
  const r = await call(() => get(`/api/v1/draft?${query}`));
  if (!r || !r.res.ok) return null;
  const body = textOf((r.body as Record<string, unknown>)["body"]);
  return body.trim() ? body : null;
}

/**
 * What the doorman may buy on the sender's behalf, and how much of it (FR-3.4).
 *
 * No price. The server stopped sending one (PRD v3.3 §1.5a, owner decision
 * 2026-08-21): consent happens when the plan is bought, the drawdown inside it
 * is quiet, and the accounting arrives afterwards — the receipt itemises the
 * work, the plan screen carries the share of the period it came out of. A
 * per-item money field here would be worse than useless: the wire no longer
 * carries one, so it would parse to 0 and put a free-of-charge price on screen.
 */
export type WorkItem = { kind: string; unit: string };

/**
 * WHERE THE PERSON STANDS RELATIVE TO THE DOOR, and the only party that moves
 * it is the recipient (`docs/conversation-model.md` §3). It replaces `status`
 * outright: a gate says who decided, never that a conversation ended. One
 * enum, shared with the server through `@nob/schemas`.
 */
export type KnockGate = SharedKnockGate;

const GATES: readonly KnockGate[] = SharedKnockGateSchema.options;

/**
 * A gate off the wire. An unreadable one reads `at_door`, which is the value
 * that claims nothing: nobody has decided, so the screen keeps the composer and
 * asks the server again rather than closing somebody's conversation on a guess.
 */
export function gateOf(value: unknown): KnockGate {
  return typeof value === "string" && (GATES as readonly string[]).includes(value)
    ? (value as KnockGate)
    : "at_door";
}

export type Knock = {
  id: string;
  gate: KnockGate;
  /** The doorman has published a read (§2: `assessment_seq > 0`). */
  assessed: boolean;
  /** The guard stopped the doorman listening (§8) — a safety stop, not a verdict. */
  guard_closed: boolean;
  max_turns: number;
  turns_used: number;
};

/**
 * What the sender may know about the shape of this hearing, and it carries NO
 * FIGURE ABOUT THE DOOR (economics-wire §5.1).
 *
 * Everything that used to live here is gone and none of it is coming back:
 * `hearing_credits`, `ceiling_credits`, `ceiling_display`,
 * `spent_at_this_door_credits`, `door_spend_cap_credits`, `price_band`,
 * `staked_credits`, `grant_applied`. A door sets no price (ruling C3), the
 * sender sets no ceiling, and the Founder's Grant is deleted.
 *
 * `balance_credits` left with the balance (ruling R-0916-1): there is no wallet
 * to have one. A sender's own plan usage is on their plan screen, as a share of
 * the period, and it was never a fact about this door. `difficulty` is prose;
 * `work_catalogue` names kinds and their units and no price.
 */
export type KnockEconomics = {
  difficulty: string;
  work_catalogue: WorkItem[];
};

export type KnockStarted = {
  knock: Knock;
  economics: KnockEconomics;
  /**
   * The doorman's first line. Rendered as speech, not as a receipt. Empty when
   * the knocker spoke first (`knocker_speaks_first`): there is none.
   */
  opening_message: string;
  /** The server's fixed AI disclosure, rendered above the first line. */
  disclosure: string;
};

/**
 * Every refusal the knock can meet (§5.3), each carrying what the screen has
 * to say back. A refusal without its number would leave the sender guessing.
 */
export type KnockFailure =
  | {
      /**
       * THE MONEY REFUSAL (ToU 2.2 §5.3). The shortfall and the packs left
       * with the credit product (ruling R-0916-1) — there is nothing to be
       * short OF — and what makes this actionable instead are the three roads
       * the server says exist. A 402 never states a plan capacity and never
       * carries a per-tier quantity.
       */
      code: "PLAN_EXHAUSTED";
      exhausted: HearingRoads;
    }
  /** The other money refusal, a 403: a dispute is open (§8.9). Nothing to offer. */
  | { code: "PAYMENT_RISK_SUSPENDED" }
  /**
   * No `KNOCK_ACTIVE`. There is exactly one knock per sender and door (§2), so
   * knocking where a row already exists is neither an error nor a new row: the
   * server answers with the conversation, and the next words are its next turn.
   */
  | { code: "DOOR_CLOSED" }
  | { code: "OWN_DOOR" }
  | { code: "RATE_LIMITED" }
  | { code: "VALIDATION_ERROR" }
  | { code: "UNAUTHENTICATED" }
  | { code: "UNKNOWN" };

export type KnockStartResult =
  { ok: true; started: KnockStarted } | { ok: false; failure: KnockFailure };

function knockFailure(status: number, body: RawError): KnockFailure {
  const code = body.error?.code;
  const d = detailsOf(body);

  if (status === 401) return { code: "UNAUTHENTICATED" };

  switch (code) {
    case "PLAN_EXHAUSTED":
      return { code: "PLAN_EXHAUSTED", exhausted: normaliseHearingRoads(d) };
    case "PAYMENT_RISK_SUSPENDED":
      return { code: "PAYMENT_RISK_SUSPENDED" };
    // No `CEILING_BELOW_FLOOR` and no `DOOR_SPEND_CAP` case. The first is
    // unreachable — this client sends no ceiling — and the second is DELETED
    // (economics-wire §5.1): the per-door cap protected against door-set
    // prices, and there are none.
    case "DOOR_CLOSED":
      return { code: "DOOR_CLOSED" };
    case "OWN_DOOR":
      return { code: "OWN_DOOR" };
    case "RATE_LIMITED":
      return { code: "RATE_LIMITED" };
    case "VALIDATION_ERROR":
      return { code: "VALIDATION_ERROR" };
  }

  if (status === 429) return { code: "RATE_LIMITED" };
  if (status === 400) return { code: "VALIDATION_ERROR" };
  return { code: "UNKNOWN" };
}

function normaliseWork(raw: unknown): WorkItem[] {
  if (!Array.isArray(raw)) return [];
  const items: WorkItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const w = item as Record<string, unknown>;
    const kind = typeof w["kind"] === "string" ? w["kind"] : "";
    if (!kind) continue; // work nobody can name is work nobody can ask for
    items.push({
      kind,
      unit: typeof w["unit"] === "string" ? w["unit"] : "",
    });
  }
  return items;
}

/**
 * The hold is placed by this call, so a knock that cannot be read back is a
 * knock that did not happen as far as the screen is concerned — never a
 * half-started one.
 *
 * KNOCKING WHERE A ROW ALREADY EXISTS IS THE SAME CALL (§2 and §12's first
 * scenario). There is one knock per sender and door for as long as both people
 * exist, so the server answers 200 with the conversation that is already open
 * instead of 201 with a new one, in the same body shape — and the words typed
 * next land as its next turn. Nothing here branches on which it was.
 */
export async function startKnock(input: KnockStartInput): Promise<KnockStartResult> {
  const profile: Record<string, string> = {};
  if (input.sender_profile.reply_to_email.trim()) {
    profile["reply_to_email"] = input.sender_profile.reply_to_email.trim();
  }
  if (input.sender_profile.booking_url) profile["booking_url"] = input.sender_profile.booking_url;
  if (input.sender_profile.company_url) profile["company_url"] = input.sender_profile.company_url;

  const r = await call(() =>
    post("/api/v1/knock/start", {
      door_handle: bareHandle(input.door_handle),
      ...(input.anon_key ? { anon_key: input.anon_key } : {}),
      ...(input.knocker_speaks_first ? { knocker_speaks_first: true } : {}),
      sender_profile: profile,
      // NO CEILING, AND NOTHING TO RAISE ONE TO. The server refuses one
      // outright (400) and places a hold covering the worst case a hearing can
      // cost (ruling C3): a hearing that opened can always be finished, and the
      // mid-conversation top-up wall is gone with the ceiling that caused it.
      //
      // `accept_terms` still rides because the terms were accepted — at the
      // checkout that started the plan, which is the consent this knock's work
      // is metered against.
      accept_terms: true,
    }),
  );
  if (!r) return { ok: false, failure: { code: "UNKNOWN" } };
  if (!r.res.ok) return { ok: false, failure: knockFailure(r.res.status, r.body as RawError) };

  const b = r.body as Record<string, unknown>;
  const k = (b["knock"] && typeof b["knock"] === "object" ? b["knock"] : {}) as Record<
    string,
    unknown
  >;
  const e = (b["economics"] && typeof b["economics"] === "object" ? b["economics"] : {}) as Record<
    string,
    unknown
  >;
  const opening = b["opening_message"];
  const content =
    opening &&
    typeof opening === "object" &&
    typeof (opening as { content?: unknown }).content === "string"
      ? (opening as { content: string }).content
      : "";

  const id = typeof k["id"] === "string" ? k["id"] : "";
  if (!id) return { ok: false, failure: { code: "UNKNOWN" } };

  return {
    ok: true,
    started: {
      knock: {
        id,
        gate: gateOf(k["gate"]),
        assessed: k["assessed"] === true,
        guard_closed: k["guard_closed"] === true,
        max_turns: num(k["max_turns"], 5),
        turns_used: num(k["turns_used"]),
      },
      economics: {
        difficulty: textOf(e["difficulty"]),
        work_catalogue: normaliseWork(e["work_catalogue"]),
      },
      opening_message: content,
      // The AI disclosure, for a knock that has just opened and will never read
      // the transcript GET that also carries it (R-0914-1 (11)).
      disclosure: typeof b["disclosure"] === "string" ? (b["disclosure"] as string) : "",
    },
  };
}

// --- reading a knock in flight (M9, PRD §5.3 / §5.4 hard rule 5) ------------

/**
 * The sender's own view of their knock — the gate, the read and the turns, and
 * structurally nothing else (`toSenderView`, §5.4 hard rule 5).
 * No score, no percentile, no rank, no summary written about them. Nothing here
 * invents a field the server did not send.
 *
 * `turns_used`/`max_turns` are the INTERROGATION meter and nothing more
 * (conversation model §6.1): they stop meaning anything once `assessed`, and
 * the client stops counting there.
 */
export type KnockView = {
  id: string;
  gate: KnockGate;
  assessed: boolean;
  guard_closed: boolean;
  turns_used: number;
  max_turns: number;
  /**
   * NO FIGURE AT ALL, ONLY WHETHER THE HEARING HAS SETTLED (ruling R-0916-1).
   * `charged_units` went with the wallet that was charged: a unit count on a
   * sender's screen is a capacity in disguise (ruling C2), and the accounting a
   * sender may have lives on their plan screen as a share of the period.
   */
  economics: { settled: boolean };
  /**
   * What this sender put into the hearing (§6.3), for a reload to render back —
   * with the moment each went in, so it renders where it was sent (wave 82).
   */
  attachments: (Attachment & { created_at: string | null })[];
  /**
   * The doorman's work in this hearing, as the stream showed it, with the
   * moment each piece started — so a reload draws its line where the live
   * thread drew it (wave 82). Labels and counts; never a claim or a figure.
   */
  work: KnockWork[];
};

/** One piece of the doorman's work, read back (`toSenderWorkLine` on the server). */
export type KnockWork = {
  id: string;
  kind: string;
  label: string;
  units: number;
  progress: number | null;
  done: boolean;
  units_executed: number;
  outcome: Record<string, number | boolean>;
  created_at: string | null;
};

function normaliseKnockWork(raw: unknown): KnockWork[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): KnockWork[] => {
    if (!entry || typeof entry !== "object") return [];
    const w = entry as Record<string, unknown>;
    const id = textOf(w["work_item_id"]);
    if (!id) return [];
    const outcome: Record<string, number | boolean> = {};
    const o = w["outcome"] && typeof w["outcome"] === "object" ? w["outcome"] : {};
    for (const [key, v] of Object.entries(o as Record<string, unknown>)) {
      if ((typeof v === "number" && Number.isFinite(v)) || typeof v === "boolean") outcome[key] = v;
    }
    return [
      {
        id,
        kind: textOf(w["kind"]),
        label: textOf(w["label"]),
        units: num(w["units_executed"]),
        progress: null,
        done: w["done"] === true,
        units_executed: num(w["units_executed"]),
        outcome,
        created_at: optTextOf(w["created_at"]),
      },
    ];
  });
}

/** A knock that is not yours is a knock that does not exist (404, either way). */
/**
 * `RESULT_HELD` is the one of these that is not a fault (ruling R-0921-2
 * (6)): the hearing is decided and the standing is waiting on the sender's
 * own allowance. Every screen that reads a standing has to tell it apart from
 * the three that ARE faults, or it offers a retry for something retrying will
 * not fix.
 */
export type KnockReadFailureCode =
  "UNAUTHENTICATED" | "KNOCK_NOT_FOUND" | "RESULT_HELD" | "UNKNOWN";

export type KnockReadResult<T> = ({ ok: true } & T) | { ok: false; code: KnockReadFailureCode };

export function knockReadFailure(
  status: number,
  code?: unknown,
): { ok: false; code: KnockReadFailureCode } {
  if (status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (status === 404) return { ok: false, code: "KNOCK_NOT_FOUND" };
  /**
   * **THE RESULT IS HELD, NOT MISSING** (ruling R-0921-2 (6)). The read ran,
   * the hearing is decided, and what is withheld is the standing until the
   * sender adds room. A screen told "could not be fetched" would send them
   * looking for a fault; told this, it draws the same three roads a paused
   * hearing draws, which is the thing they can act on.
   */
  if (status === 402 && code === "RESULT_HELD") return { ok: false, code: "RESULT_HELD" };
  return { ok: false, code: "UNKNOWN" };
}

export async function fetchKnock(id: string): Promise<KnockReadResult<{ knock: KnockView }>> {
  const r = await call(() => get(`/api/v1/knock/${encodeURIComponent(id)}`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return knockReadFailure(r.res.status);

  const b = r.body as Record<string, unknown>;
  const knockId = typeof b["id"] === "string" ? b["id"] : id;
  const e = (b["economics"] && typeof b["economics"] === "object" ? b["economics"] : {}) as Record<
    string,
    unknown
  >;
  return {
    ok: true,
    knock: {
      id: knockId,
      // A gate that will not read is `at_door`: the screen asks the server
      // again rather than closing a conversation on a guess (see `gateOf`).
      gate: gateOf(b["gate"]),
      assessed: b["assessed"] === true,
      guard_closed: b["guard_closed"] === true,
      turns_used: num(b["turns_used"]),
      max_turns: num(b["max_turns"]),
      economics: { settled: e["settled"] === true },
      // What this sender attached. The transcript does not carry files — a
      // document is not a turn, and a voice note sent AS one is named there
      // only by id (wave 76) — so a reload finds its players here or nowhere.
      attachments: Array.isArray(b["attachments"])
        ? (b["attachments"] as unknown[]).flatMap((a) => {
            if (!a || typeof a !== "object") return [];
            const raw = a as Record<string, unknown>;
            const file = toAttachment(raw);
            return file ? [{ ...file, created_at: optTextOf(raw["created_at"]) }] : [];
          })
        : [],
      work: normaliseKnockWork(b["work"]),
    },
  };
}

/**
 * One conversation in the sender's own list (`GET /api/v1/knocks`).
 *
 * The same allowlist as `KnockView`, plus the door it was with and the M12 band
 * PHRASE for the ones that have been scored. There is no score here and there is
 * no place to put one: the server sends the sentence, never the number (§5.4
 * hard rule 5), and this file invents nothing the server did not send.
 */
export type MyKnock = {
  id: string;
  door_handle: string;
  door_display_name: string;
  /**
   * The recipient's own decision, and the only thing that moves (§3). `declined`
   * is what "they passed on this one" is now — the sender was told in this
   * knock's own transcript, and this only lets the list agree with it.
   */
  gate: KnockGate;
  /** The doorman published a read. Until then there is no standing to name. */
  assessed: boolean;
  /** The guard stopped the doorman listening (§8): no composer, one line, no verdict. */
  guard_closed: boolean;
  /**
   * The wall stopped this hearing and it is waiting for room (ruling R-0921-2
   * (5)). Still at the door: the gate has not moved and nobody has decided
   * anything — what it is waiting for is the sender's own allowance.
   */
  paused: boolean;
  turns_used: number;
  max_turns: number;
  updated_at: string | null;
  /**
   * The sender put their own copy away (ruling R-0828-1). Their mark on their
   * side; the door's owner has one of their own and neither can see the other's.
   */
  archived: boolean;
  /** When the conversation opened. There is exactly one of them per door (§2). */
  created_at: string | null;
};

export type MyKnocksResult =
  { ok: true; knocks: MyKnock[] } | { ok: false; code: "UNAUTHENTICATED" | "UNKNOWN" };

/** The sender's conversations, newest activity first. Needs a session. */
export async function fetchMyKnocks(): Promise<MyKnocksResult> {
  const r = await call(() => get("/api/v1/knocks"));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  }
  const raw = (r.body as Record<string, unknown>)["knocks"];
  if (!Array.isArray(raw)) return { ok: true, knocks: [] };
  return {
    ok: true,
    // Normalised the way every other list here is: a row with no id is not a
    // conversation, and a missing name falls back to the address rather than blank.
    knocks: raw.flatMap((entry) => {
      const knock = normaliseMyKnock(entry);
      return knock ? [knock] : [];
    }),
  };
}

/**
 * One sent knock off the wire — `GET /knocks`'s rows and the OUTGOING side of a
 * thread (`GET /threads`) share it, so the two cannot read the same row
 * differently. Exported for the threads parser and the seam test.
 */
export function normaliseMyKnock(entry: unknown): MyKnock | null {
  const row = (entry ?? {}) as Record<string, unknown>;
  const id = textOf(row["id"]);
  if (!id) return null;
  const handle = bareHandle(textOf(row["door_handle"]));
  return {
    id,
    door_handle: handle,
    door_display_name: textOf(row["door_display_name"]) || `@${handle}`,
    // A gate that will not read is `at_door`, exactly as the single-knock read
    // does it: the screen asks the server rather than closing somebody's
    // conversation on a guess.
    gate: gateOf(row["gate"]),
    assessed: row["assessed"] === true,
    guard_closed: row["guard_closed"] === true,
    paused: row["paused"] === true,
    turns_used: num(row["turns_used"]),
    max_turns: num(row["max_turns"]),
    updated_at: optTextOf(row["updated_at"]),
    archived: row["archived"] === true,
    created_at: optTextOf(row["created_at"]),
  };
}

/**
 * Who said it. `notice` is the room speaking, not either party in it.
 *
 * `recipient` is the FOURTH voice and the newest (owner, 2026-08-26): the person
 * behind the door, answering. It only ever appears once they have let the
 * sender through, and its arrival in the transcript is the only thing that tells
 * a sender the door opened — there is no status field for it and there must not
 * be one, because the product does not know that a reply is a yes. The reply
 * itself is the yes.
 *
 * THERE IS NO FIFTH (ruling R-0910-11 rule 2). A doorman line the door's owner
 * asked their Nob to put is a `doorman` line and nothing more: the server does
 * not send a mark, this does not parse one, and the room has no way to draw one
 * even by accident. The owner's own desk is where that fact lives
 * (`lib/api/inbox.ts`).
 */
export type MessageRole = "sender" | "doorman" | "notice" | "recipient";

export type KnockMessage = {
  seq: number;
  role: MessageRole;
  content: string;
  created_at: string | null;
  /** The sender's own voice note this line was said in, or null (wave 76). */
  voice_attachment_id: string | null;
};

export const ROLES: MessageRole[] = ["sender", "doorman", "notice", "recipient"];

/**
 * The transcript, and the AI disclosure that stands above it (EU AI Act Art. 50;
 * ruling R-0914-1 (11)).
 *
 * `disclosure` IS NOT A MESSAGE and is deliberately not parsed into one: it is
 * the server's fixed line, the same on every knock, and it renders above the
 * first row rather than as one. Empty string when the payload could not be read,
 * which draws nothing — a disclosure this side invented would be worth less than
 * no disclosure at all.
 */
export async function fetchKnockMessages(
  id: string,
): Promise<KnockReadResult<{ messages: KnockMessage[]; disclosure: string }>> {
  const r = await call(() => get(`/api/v1/knock/${encodeURIComponent(id)}/messages`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return knockReadFailure(r.res.status);

  const body = r.body as { messages?: unknown; disclosure?: unknown };
  const disclosure = typeof body.disclosure === "string" ? body.disclosure : "";
  const raw = body.messages;
  const messages: KnockMessage[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const m = item as Record<string, unknown>;
      const role = ROLES.find((known) => known === m["role"]);
      const content = typeof m["content"] === "string" ? m["content"] : "";
      // A line with no speaker or nothing said is not a line of the transcript.
      if (!role || !content.trim()) continue;
      messages.push({
        seq: num(m["seq"]),
        role,
        content,
        created_at: typeof m["created_at"] === "string" ? m["created_at"] : null,
        voice_attachment_id:
          typeof m["voice_attachment_id"] === "string" ? m["voice_attachment_id"] : null,
      });
    }
  }
  // Order is the server's contract; sorting locally costs nothing and means a
  // transcript that arrives out of order still reads as a conversation.
  messages.sort((a, b) => a.seq - b.seq);
  return { ok: true, messages, disclosure };
}

/**
 * The sender writing back, on a knock that was let through (owner 2026-08-26).
 *
 * NOT `streamTurn`, and the two must never be confused. A turn is answered by a
 * model, costs the sender's plan, and can be refused for a dozen reasons this
 * has none of. This posts one message to a person and gets a sequence number
 * back.
 *
 * `code` carries the SERVER'S code when there is one, and there is one that
 * matters: `KNOCK_NOT_FOUND`. Every refusal on this route is a 404 by design —
 * the server will not say WHY a knock cannot be written on (see the route) —
 * but the sender's own screen may read it as what it always is HERE, a door
 * that is not open, rather than as the network being down. `"UNKNOWN"` is a
 * 5xx or a dead connection and nothing else.
 */
export type WriteBackResult = { ok: true; seq: number } | { ok: false; code: string };

export async function sendKnockMessage(id: string, message: string): Promise<WriteBackResult> {
  const r = await call(() => post(`/api/v1/knock/${encodeURIComponent(id)}/messages`, { message }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    const code = (r.body as RawError).error?.code;
    return { ok: false, code: typeof code === "string" && code ? code : "UNKNOWN" };
  }
  return { ok: true, seq: num((r.body as Record<string, unknown>)["seq"]) };
}

// --- the receipt (M11, PRD §5.3 / FR-3.5) -----------------------------------

/** One thing the doorman DID. Units of work — pages, claims, sources, turns — never a price. */
export type ReceiptWorkLine = { label: string; units: number };

/**
 * The receipt (FR-3.5), and it is WORK PRODUCTS AND NOT ONE FIGURE OF MONEY
 * (ruling R-0916-1, ToU 2.2 §5.3 — "within a knock, work is described in terms
 * of work products, not monetary amounts"):
 *
 *   - `work` — what the doorman did, itemised, in its own units. A page read is
 *     a page read whatever paid for it.
 *   - `covered_by_plan` — a sentence, so that an absent charge is read as the
 *     ordinary case rather than as an error.
 *
 * `charged_units` and `released_credits` are deleted with the wallet that held
 * them; `lines[].credits` went before them when work stopped being priced item
 * by item. There is nothing left on this payload that a reader could divide
 * back into a capacity.
 *
 * No score, no percentile, no band: this is the sender's payload (§5.4 rule 5).
 */
export type Receipt = {
  work: ReceiptWorkLine[];
  /** True when the knocker's own plan paid for the whole hearing. */
  covered_by_plan: boolean;
  receipts_earned: {
    claims_verified: number;
    claims_refuted: number;
    pages_reviewed: number;
    sources_checked: number;
  };
};

function normaliseWorkLines(raw: unknown): ReceiptWorkLine[] {
  if (!Array.isArray(raw)) return [];
  const lines: ReceiptWorkLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const l = item as Record<string, unknown>;
    const label = typeof l["label"] === "string" ? l["label"] : "";
    // A line with nothing to name is a line nobody can read.
    if (!label.trim()) continue;
    lines.push({ label, units: num(l["units"]) });
  }
  return lines;
}

/** A receipt off the wire, coerced into shape — the same one `standing` carries inline. */
function normaliseReceipt(raw: unknown): Receipt {
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const earned = (
    b["receipts_earned"] && typeof b["receipts_earned"] === "object" ? b["receipts_earned"] : {}
  ) as Record<string, unknown>;
  return {
    work: normaliseWorkLines(b["work"]),
    covered_by_plan: b["covered_by_plan"] === true,
    receipts_earned: {
      claims_verified: num(earned["claims_verified"]),
      claims_refuted: num(earned["claims_refuted"]),
      pages_reviewed: num(earned["pages_reviewed"]),
      sources_checked: num(earned["sources_checked"]),
    },
  };
}

export async function fetchReceipt(id: string): Promise<KnockReadResult<{ receipt: Receipt }>> {
  const r = await call(() => get(`/api/v1/knock/${encodeURIComponent(id)}/receipt`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return knockReadFailure(r.res.status);
  return { ok: true, receipt: normaliseReceipt(r.body) };
}

/**
 * The scored knock's standing (M12, §2.4.2). The band is the resolution — a
 * plain statement in `band_label`, never a number — with the improvement, the
 * receipt carried inline, and what a re-knock would be. Produced by
 * `toSenderView()`: no score, no percentile, no rank, no dimensions (§5.4 r5).
 */
export type StandingBand =
  | "TOP_5"
  | "TOP_25"
  | "MIDDLE"
  | "BOTTOM_25"
  | "BOTTOM_5"
  | "TIER_STRONG"
  | "TIER_REASONABLE"
  | "TIER_WEAK";

const STANDING_BANDS: readonly StandingBand[] = [
  "TOP_5",
  "TOP_25",
  "MIDDLE",
  "BOTTOM_25",
  "BOTTOM_5",
  "TIER_STRONG",
  "TIER_REASONABLE",
  "TIER_WEAK",
];

/**
 * NO `reknock` BLOCK, and there is nothing to knock again at (§10). One
 * conversation exists per sender and door, it never closes, and the sender's
 * composer is still under this card: the way on is to keep talking.
 */
export type Standing = {
  band: StandingBand;
  /** The band as a sentence: "Top 22% at this door this week", or a cold-start tier line. */
  band_label: string;
  in_recap: boolean;
  recap_note: string | null;
  improvement: string;
  /**
   * THE SERVER'S DECISION THAT "WHAT WOULD MOVE YOU UP" MAY BE SHOWN (ruling
   * R-0925-1 (3)): only where the knocker fell short on something they can
   * honestly supply. False means the line is not drawn, whatever it says.
   */
  improvement_shown: boolean;
  receipt: Receipt;
  disclaimer: string;
};

/** An unknown band is a knock we cannot place; treated as the middle, never guessed up. */
function bandOf(value: unknown): StandingBand {
  return typeof value === "string" && (STANDING_BANDS as readonly string[]).includes(value)
    ? (value as StandingBand)
    : "MIDDLE";
}

/**
 * A standing off the wire — the `standing` SSE event and the resume endpoint
 * share it. The event fires on EVERY published assessment, re-assessments
 * included (§5.2), and this parser is indifferent to which one it is: a
 * standing is a standing, and the screen simply draws the latest.
 */
export function normaliseStanding(raw: unknown): Standing {
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  // The server emits the band sentence as `label` (PRD §5.4) and carries
  // `receipts_earned` as a SIBLING of `receipt`, not nested — accept either
  // spelling and fold the sibling in so the earned line is never dropped.
  const receiptRaw = (
    b["receipt"] && typeof b["receipt"] === "object" ? b["receipt"] : {}
  ) as Record<string, unknown>;
  const receiptSource =
    receiptRaw["receipts_earned"] === undefined && b["receipts_earned"] !== undefined
      ? { ...receiptRaw, receipts_earned: b["receipts_earned"] }
      : receiptRaw;
  return {
    band: bandOf(b["band"]),
    band_label: textOf(b["band_label"] ?? b["label"]),
    in_recap: b["in_recap"] === true,
    recap_note: optTextOf(b["recap_note"]),
    improvement: textOf(b["improvement"]),
    improvement_shown: b["improvement_shown"] === true,
    receipt: normaliseReceipt(receiptSource),
    disclaimer: textOf(b["disclaimer"]),
  };
}

/**
 * The standing for an assessed knock (M12). The `standing` SSE event carries it
 * inline as each assessment is published, so this endpoint is the resume path:
 * a tab that comes back to an assessed knock reads it here.
 *
 * THERE IS NO `POST /conclude` (§10). A sender with nothing left to add says so
 * to the doorman, and that is a turn.
 */
export async function fetchStanding(id: string): Promise<KnockReadResult<{ standing: Standing }>> {
  const r = await call(() => get(`/api/v1/knock/${encodeURIComponent(id)}/standing`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) {
    const body = (r.body ?? {}) as { error?: { code?: unknown } };
    return knockReadFailure(r.res.status, body.error?.code);
  }
  return { ok: true, standing: normaliseStanding(r.body) };
}

/**
 * The door a visit arrived to knock at, out of `?knock=<handle>`. It is read
 * once and then kept in storage, because sign-in and checkout both leave the
 * origin and come back to a bare `/`.
 */
export function knockQueryTarget(
  search: string = typeof window === "undefined" ? "" : window.location.search,
): string | null {
  const handle = new URLSearchParams(search).get("knock");
  const bare = handle ? bareHandle(handle) : "";
  return bare ? bare : null;
}

export function clearKnockQuery(): void {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("knock")) return;
  url.searchParams.delete("knock");
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}
